import { ACTIONS, AppContext } from "../core/context";
import { materialLibrary, meshLibrary, productLibrary } from "../data";
import { adjustCameraView } from "../scene/camera";
import { hideAllHighlights, hideHighlight, toggleMeshVisibility } from "../scene/highlight";
import { getPositionConnectorInfo, getTargetPosition, getZeroPlaneVector } from "../scene/pointer";
import { pauseRender, resumeRender } from "../scene/renderer";
import { ConnectorInfo, MeshVisibility } from "../types/type_def";
import { MathHelper } from "../utils/math";
import { hideEntityActions } from "./actions";
import { canInsert, createEntityConnectors, getMatchingConnectors, getProximityConnectorInfo, hideAvailableConnectors, removeEntityConnections, updateAvailableConnectors } from "./connectors";
import { constructModule, getProductVariant } from "./construction";
import { appendEntity, deleteEntity, getCurrentEntity, insertEntity, instantiateEntity, instantiateModule } from "./entities";
import { constructLegs, createLegAttachments } from "./legs";
import { onProgress } from "./progress";

export function showGhostEntity(ctx: AppContext, connectorInfos: ConnectorInfo[], isInsert: boolean) {
    if (!ctx.currentEntityNode || connectorInfos.length === 0) return;
   
    var connectorInfo = connectorInfos[0];

    var targetUniqueId = connectorInfos.map(ci => (ci.connectorNode as BABYLON.AbstractMesh).uniqueId).join("_");        
    if (ctx.ghostEntityNode && ctx.ghostConnectorUniqueId === targetUniqueId) return;

    ctx.ghostConnectorUniqueId = targetUniqueId;

    // CLONE PHASE: only when dragged entity changed
    if (!ctx.ghostEntityNode || ctx.ghostCachedEntityUniqueId !== ctx.currentEntityNode.uniqueId) {
        if (ctx.ghostEntityNode) { ctx.ghostEntityNode.dispose(); }
        ctx.ghostCachedEntityUniqueId = ctx.currentEntityNode.uniqueId;
        ctx.ghostEntityNode = new BABYLON.TransformNode("ghost_entity", ctx.scene);     
        ctx.ghostEntityNode.setPivotMatrix(ctx.currentEntityNode.getPivotMatrix());

        const entityBounds = ctx.currentEntityNode.getChildMeshes(true, x => x.id.includes("entitybounds"))[0];

        let bi = entityBounds.getBoundingInfo()
        let bb = bi.boundingBox;
        let box = BABYLON.MeshBuilder.CreateBox("ghost_entitybounds", { width: bb.extendSize.x*2, height: bb.extendSize.y*2, depth: bb.extendSize.z*2 }, ctx.scene);
        box.parent = ctx.ghostEntityNode;
        box.position = new BABYLON.Vector3(entityBounds.position.x, bb.extendSize.y,entityBounds.position.z);
        box.renderingGroupId = 0;
        box.overlayColor = BABYLON.Color3.FromHexString("#0461b0");                
        box.renderOverlay = true;
        box.visibility = 0.2;
    
        ctx.ghostChildConnectorMesh = BABYLON.MeshBuilder.CreateBox("ghost_connector", { size: 0.01 }, ctx.scene);
        ctx.ghostChildConnectorMesh.parent = ctx.ghostEntityNode;
        ctx.ghostChildConnectorMesh.visibility = 0;          
    }

    // REPOSITION PHASE: runs every time snap target changes
    ctx.ghostEntityNode.setEnabled(true);
    ctx.ghostEntityNode.position = ctx.currentEntityNode.position.clone();
    ctx.ghostEntityNode.rotation = ctx.currentEntityNode.rotation.clone();

    var sourceConnector = connectorInfo.sourceConnectorNode as BABYLON.AbstractMesh;
    if (sourceConnector) {
        ctx.ghostChildConnectorMesh.position = sourceConnector.position.clone();
        ctx.ghostChildConnectorMesh.rotation = sourceConnector.rotation.clone();                     
    }

    ctx.ghostChildConnectorMesh.computeWorldMatrix(true);   
    ctx.ghostEntityNode.computeWorldMatrix(true);

    const localForward = new BABYLON.Vector3(0, 0, 1);

    var parentNormal = BABYLON.Vector3.TransformNormal(localForward, (connectorInfo.connectorNode as BABYLON.AbstractMesh).getWorldMatrix());
    parentNormal.y = 0;
    if (parentNormal.length() > 0.001) { parentNormal.normalize(); }

    var childNormal = BABYLON.Vector3.TransformNormal(localForward, ctx.ghostChildConnectorMesh.getWorldMatrix());
    childNormal.y = 0;
    if (childNormal.length() > 0.001) { childNormal.normalize(); }

    var targetNormal = parentNormal.negate();
    const rawAngle = Math.atan2(targetNormal.x, targetNormal.z) - Math.atan2(childNormal.x, childNormal.z);
    const step = Math.PI / 2;
    const newRotationAngle = MathHelper.normalizePi(Math.round(rawAngle / step) * step);

    ctx.ghostEntityNode.rotation.addInPlace(new BABYLON.Vector3(0, newRotationAngle, 0));
    ctx.ghostChildConnectorMesh.computeWorldMatrix(true);

    if (isInsert && connectorInfos.length >= 2) {
        var target1World = (connectorInfos[0].connectorNode as BABYLON.AbstractMesh).getAbsolutePosition();
        var target2World = (connectorInfos[1].connectorNode as BABYLON.AbstractMesh).getAbsolutePosition();
        var targetMid = target1World.add(target2World).scale(0.5);

        ctx.ghostEntityNode.computeWorldMatrix(true);
        var hb = ctx.ghostEntityNode.getHierarchyBoundingVectors(true);
        var ghostCenter = hb.min.add(hb.max).scale(0.5);

        ctx.ghostEntityNode.position = ctx.ghostEntityNode.position.add(
            new BABYLON.Vector3(targetMid.x - ghostCenter.x, 0, targetMid.z - ghostCenter.z)
        );
    } else {
        var positionDiff = (connectorInfo.connectorNode as BABYLON.AbstractMesh).getAbsolutePosition()
            .subtract(ctx.ghostChildConnectorMesh.getAbsolutePosition());
        ctx.ghostEntityNode.position = ctx.ghostEntityNode.position.add(positionDiff);
    }
    ctx.ghostEntityNode.computeWorldMatrix(true);
}

export function hideGhostEntity(ctx: AppContext) {
    if (ctx.ghostEntityNode) {
        ctx.ghostEntityNode.dispose();
        ctx.ghostEntityNode = null;
        ctx.ghostConnectorUniqueId = null;
        ctx.ghostCachedEntityUniqueId = null;
        ctx.ghostChildConnectorMesh = null;           
    }
}

/**
* Drag Functions
* */

//Handle Drag
export function handleDrag(ctx: AppContext) {
    
    if (ctx.currentEntityNode) {

        const entity = getCurrentEntity(ctx);
        const product = productLibrary.getProductByProductNo(entity.productNo); 

        if (entity) {
            toggleMeshVisibility(ctx, ctx.currentEntityNode, MeshVisibility.HIGHLIGHTED);              
            hideEntityActions(ctx);                            
            hideAllHighlights(ctx);

            let isPositionInfo = true;
            let connectorInfos: ConnectorInfo[] = getPositionConnectorInfo(ctx, ctx.screenPointerPosition, ctx.currentEntityNode); 
            if (connectorInfos.length === 0) {
                isPositionInfo = false;
                connectorInfos = getProximityConnectorInfo(ctx, entity, ctx.currentEntityNode);
            }                

            const isInsert = connectorInfos.length >= 2 && isPositionInfo && canInsert(ctx, product);  
                        
            if (connectorInfos.length > 0) {

                if (connectorInfos.length === 1) {

                    if (isPositionInfo) return;

                    let entity = ctx.moduleHelper.getEntityByID(ctx.currentModule, connectorInfos[0].entityNode.id)
                    if (entity.connections.filter(x => x.id === connectorInfos[0].connectorNode.name).length > 0) 
                        return;
                }

                var connectorInfo = connectorInfos[0];
                var connectorEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, connectorInfo.entityNode.id);
                var connector = connectorEntity.connectors.find(c => { return c.id === connectorInfo.connectorNode.name });
                var matchingConnectors = getMatchingConnectors(ctx, connector, entity, connectorEntity);

                var childConnector = ctx.currentEntityNode.getChildMeshes(true, function (m) { return matchingConnectors.includes(m.name) })[0];
                if (childConnector) {
                  
                    if (connectorInfo.isEnabled) {    
                        //Show ghost mesh                     
                        showGhostEntity(ctx, connectorInfos,isInsert);                            
                    }
                  
                    if (!ctx.currentConnector || ctx.currentConnector.uniqueId !== connectorInfo.connectorNode.uniqueId) {
                        ctx.currentConnector = connectorInfo.connectorNode as BABYLON.AbstractMesh;
                    }
                }

            } else {       
                
                hideAllHighlights(ctx);
                hideGhostEntity(ctx);
               // dimGhostEntity();
            }
        }
    }
}

//Handle Drag End
export async function handleDragEnd(ctx: AppContext) {

    var entity = getCurrentEntity(ctx);
    if (entity) {

        let product = productLibrary.getProductByProductNo(entity.productNo);
        let noInsert = false;

        //No Insert
        if ([148881,148882,157547,157546].includes(product.series)) {
            noInsert = true;
        }            

        toggleMeshVisibility(ctx, ctx.currentModuleNode, MeshVisibility.VISIBLE);          
        hideEntityActions(ctx);
        hideAllHighlights(ctx);           
   
        let isPositionInfo = true;
        let connectorInfos: ConnectorInfo[] = getPositionConnectorInfo(ctx, ctx.screenPointerPosition, ctx.currentEntityNode); 
        if (connectorInfos.length === 0) {
            isPositionInfo = false;
            connectorInfos = getProximityConnectorInfo(ctx, entity, ctx.currentEntityNode);
        }

        let matchingConnectors = [];
        
        if (connectorInfos.length > 0) {

            //if (connectorInfos.length === 1 && isPositionInfo) {
           //     return;
           // }

            let connectorEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, connectorInfos[0].entityNode.id);
            let connector = connectorEntity.connectors.find(c => { return c.id === connectorInfos[0].connectorNode.name });
            matchingConnectors = getMatchingConnectors(ctx, connector, entity, connectorEntity);
        }

        ctx.ui.createEntityPlacementPopup(entity, matchingConnectors).then((placement: string) => {
            
            if (connectorInfos.length > 0) {
                //Dragged over single unconnected connector
                if (connectorInfos.length === 1 || noInsert) {
                    let connectorInfo = connectorInfos[0] as ConnectorInfo;

                    if (connectorInfo.isEnabled) {
                        removeEntityConnections(ctx, entity);

                        //Append entity
                        appendEntity(ctx, entity, connectorInfo, placement);
                        ctx.currentAction = ACTIONS.DRAGOVER
                    }

                    //Dragged over already connected connectors (insert etc.)
                } else if (connectorInfos.length > 1) {

                    removeEntityConnections(ctx, entity);

                    //Insert entity
                    insertEntity(ctx, entity, connectorInfos, placement);
                    ctx.currentAction = ACTIONS.DRAGOVER
                }
            } else {

                //Entity not connected to anything yet (no need to reconstruct)
                //currentEntityNode.rotation.y = entity.rotation.y;

                if (entity.connections.length > 0) {
                    removeEntityConnections(ctx, entity);
                    ctx.currentAction = ACTIONS.DRAGOVER
                } else {

                    hideAllHighlights(ctx);
                    hideEntityActions(ctx);
            
                    pauseRender(ctx);
                    resumeRender(ctx).then(() => {
                        ctx.ui.handleModuleUpdating();                        
                    });
                }
            }

            //Create weights to determine best view
            //createEntityWeights(entity);

            ctx.currentEntityNode = null;

            if (ctx.currentAction == ACTIONS.DRAGOUT || ctx.currentAction == ACTIONS.DRAGOVER) {
                ctx.currentAction = ACTIONS.NONE;
                constructModule(ctx, ctx.currentModule).then(() => {
                    adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, ctx.mainCamera.alpha, ctx.mainCamera.beta, ctx.mainCamera.radius, ctx.defaultMinCameraRadius);
                })                                    
            } else {
                //mainCamera.attachControl(true, false);
                ctx.ui.handleModuleUpdating(() => {
                    adjustCameraView(ctx, ctx.mainCamera, ctx.currentModuleNode, ctx.scene, ctx.mainCamera.alpha, ctx.mainCamera.beta, ctx.mainCamera.radius, ctx.defaultMinCameraRadius);
                });
            }   
            
             // hideConnectors();
        })
    }
}

//Drag Start (Canvas)
export function dragStartCanvas(ctx: AppContext) {

    ctx.isDragging = true;        
    var entity = getCurrentEntity(ctx);

    if (entity.connections.length > 0) {
        ctx.currentAction = ACTIONS.DRAGOUT
    }

    removeEntityConnections(ctx, entity);
    ctx.currentModule.entities.filter(x => productLibrary.getProductCategory(x.category) === "UPHOLSTRY").forEach(moduleentity => {
        let product = productLibrary.getProductByProductNo(moduleentity.productNo);           
        createEntityConnectors(ctx, ctx.currentModule, moduleentity, product, ctx.scene);
    })    

    updateAvailableConnectors(ctx, ctx.currentModule, entity);     
}

//Drag (Canvas)   
export function dragCanvas(ctx: AppContext) {

    if (ctx.isDragging) {
        handleDrag(ctx);
    }
}

//Drag End (Canvas)
export function dragEndCanvas(ctx: AppContext) {

    if (ctx.isDragging) {

        ctx.isDragging = false;
        handleDragEnd(ctx);
        hideAvailableConnectors(ctx);
        hideGhostEntity(ctx);
    }
}

//Drag Start (UI)
export function dragUIStart(ctx: AppContext, evt) {

    const entity = getCurrentEntity(ctx);
    if (entity) {
        if (ctx.isPlacing) {    
            ctx.isPlacing = false;
            deleteEntity(ctx, entity); 
            hideAvailableConnectors(ctx);   
        }            
    }           

    hideHighlight(ctx, ctx.currentEntityNode);
    ctx.currentEntityNode = null;

    let elem = evt.operation.source.element as HTMLElement;
    let productNumber = elem.getAttribute("data-productnumber")
    
   // let productElem = elem.ancestor("li");
   // elem.addClass("dragged");

    ctx.meshLoading = false;
    ctx.isDragging = true;

   // mainCamera.detachControl();

   // let productNumber = productElem.getData("productnumber") as string;
    ctx.currentProduct = productLibrary.getProductByProductNo(productNumber);
    
}

//Drag (UI)
export function dragUI(ctx: AppContext, evt) {

    hideEntityActions(ctx);

    ctx.screenPointerPosition = new BABYLON.Vector2(evt.to.x, evt.to.y - 60);
    ctx.scenePointerPosition = getTargetPosition(ctx, ctx.screenPointerPosition.x, ctx.screenPointerPosition.y);
    ctx.dragPosition = getZeroPlaneVector(ctx, ctx.mainCamera.position, ctx.scenePointerPosition);

    var distance = BABYLON.Vector3.Distance(ctx.mainCamera.position, ctx.scenePointerPosition);

    if (!ctx.dragPosition && !distance) {
        ctx.isDragging = false;
    }

    if (ctx.isDragging) {

        //Create mesh
        if (BABYLON.Vector3.Distance(ctx.cameraTarget, ctx.dragPosition) <= distance) {

            if (!ctx.meshLoading) {
                ctx.meshLoading = true;

                //Instantiate module
                instantiateModule(ctx, ctx.currentConfiguration, ctx.currentModule, function (module, moduleNode) {

                    ctx.currentModule = module;
                    ctx.currentModuleNode = moduleNode;

                    var product = productLibrary.getProductByProductNo(ctx.currentProduct.productNo);

                    //Instatiate entity
                    instantiateEntity(ctx, module, product, null, function (instantiatedEntity, instantiatedEntityNode) {

                        var productVariant = getProductVariant(ctx, instantiatedEntity.productNo, instantiatedEntity);
                        instantiatedEntity.variantProductNo = productVariant.productNo;
                        instantiatedEntityNode.metadata.productNo = productVariant.productNo;

                        var entityBounds = instantiatedEntityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
                        entityBounds.setEnabled(true);

                        //Set current selected entity
                        ctx.currentEntityNode = instantiatedEntityNode;                        

                        //Show available connectors
                        //showAvailableConnectors(module, instantiatedEntity);
                        updateAvailableConnectors(ctx, ctx.currentModule, instantiatedEntity);

                        //Create leg attachments                          
                        createLegAttachments(ctx, module, instantiatedEntity, productVariant, ctx.scene).then(() => {
                            
                            //Create legs
                            constructLegs(ctx, module, instantiatedEntity);
                        });

                        //Load entity mesh                        
                        meshLibrary.loadMeshAsync(productVariant.productNo, instantiatedEntity, productVariant.type, "furniture/", productVariant.model + ".glb", productVariant.isFlipped, ctx.scene, false, ctx.sg, ctx.sg2, (e) => onProgress(ctx, e), function (clonedMeshEntityId, clonedMesh) {

                            let loadedEntity = ctx.moduleHelper.getEntityByID(ctx.currentModule, clonedMeshEntityId);
                            let loadedProductVariant = getProductVariant(ctx, loadedEntity.productNo, loadedEntity);

                            clonedMesh.scaling.x = loadedProductVariant.isFlipped ? -1 : 1;
                            clonedMesh.position.y = ctx.currentConfiguration.data.offsetY;
                            
                            //Set material
                            let material = materialLibrary.getMaterialByProductNumber(ctx.currentConfiguration.data["material"]);
                            if (material) {
                                materialLibrary.assignMaterial(loadedProductVariant, [clonedMesh], material, ctx.scene, function () {
                                    clonedMesh.setEnabled(true);
                                });
                            }                                

                        });                         

                    });                       
                });
            }
        }

        if (ctx.currentEntityNode) {

            //Move according to real center of entity
            var centerNode = ctx.currentEntityNode.getChildTransformNodes(false, function (m) { return m.id === "center" })[0];
            ctx.dragPosition.subtractInPlace(centerNode.position);

            ctx.currentEntityNode.setAbsolutePosition(new BABYLON.Vector3(ctx.dragPosition.x, ctx.currentEntityNode.position.y, ctx.dragPosition.z));

            handleDrag(ctx);
        }          
    }

}
